import * as React from "react";

import type { Ordering } from "@/types/base";
import type { SnapshotGridField } from "@/modules/snapshots/businessReports.api";
import { formatSnapshotGridLocalDate } from "@/modules/snapshots/grid/gridQueryPayload";
import { snapshotGridColumnKey } from "@/modules/snapshots/grid/gridColumnKeys";
import { serializeColumnFilters, type ColumnFilters, type DateFilter } from "@/modules/snapshots/grid/sortAndFilter";
import type { SnapshotViewConfiguration } from "@/modules/snapshots/snapshotViews.types";

interface Input {
  initialConfiguration?: SnapshotViewConfiguration;
  fields: SnapshotGridField[];
  hiddenColumns: string[];
  columnOrder: string[];
  noteColumnOrder: string[];
  columnFilters: ColumnFilters;
  dateFilter: DateFilter;
  ordering: Ordering;
  dateRangeTouched?: boolean;
  stableColumnOrder?: string[];
}

export const useSnapshotGridConfiguration = ({
  initialConfiguration,
  fields,
  hiddenColumns,
  columnOrder,
  noteColumnOrder,
  columnFilters,
  dateFilter,
  ordering,
  dateRangeTouched = false,
  stableColumnOrder,
}: Input) => {
  const configuration = React.useMemo<SnapshotViewConfiguration>(() => {
    const mapKeys = (values: string[]) =>
      values.map(value => snapshotGridColumnKey(value, fields)).filter((value): value is string => value !== null);
    const serializedFilters = serializeColumnFilters(columnFilters);
    const stableFilters = Object.fromEntries(
      Object.entries(serializedFilters).flatMap(([name, value]) => {
        const key = snapshotGridColumnKey(name, fields);
        return key ? [[key, value]] : [];
      }),
    );
    const fixedDateRange =
      dateFilter.selectedField && (dateFilter.startDate || dateFilter.endDate)
        ? {
            mode: "fixed" as const,
            field_key: snapshotGridColumnKey(dateFilter.selectedField, fields) ?? dateFilter.selectedField,
            start_date: dateFilter.startDate ? formatSnapshotGridLocalDate(dateFilter.startDate) : null,
            end_date: dateFilter.endDate ? formatSnapshotGridLocalDate(dateFilter.endDate) : null,
            preset: null,
          }
        : dateRangeTouched
          ? null
          : (initialConfiguration?.date_range ?? null);

    return {
      schema_version: 1,
      column_order: stableColumnOrder ?? mapKeys([...columnOrder, ...noteColumnOrder]),
      hidden_column_keys: mapKeys(hiddenColumns),
      column_filters: stableFilters,
      date_range: fixedDateRange,
      ordering: {
        ...ordering,
        order_by: snapshotGridColumnKey(ordering.order_by, fields) ?? ordering.order_by,
      },
    };
  }, [
    columnFilters,
    columnOrder,
    dateFilter,
    dateRangeTouched,
    fields,
    hiddenColumns,
    initialConfiguration?.date_range,
    noteColumnOrder,
    ordering,
    stableColumnOrder,
  ]);

  const dirty = Boolean(initialConfiguration && JSON.stringify(configuration) !== JSON.stringify(initialConfiguration));
  return { configuration, dirty };
};
